import {
  Box,
  Divider,
  Grid,
  List,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Alert, Dialog, DialogContent } from '@side/pantry';
import type { InteractionType } from '@/common/types/interaction';
import { EmptyState } from '../EmptyState';
import { MailToLink } from './components/MailToLink';
import { PackageLink } from './components/PackageLink';
import { useGetOfferPackage } from './useGetOfferPackage';
import { composeEmail, DOCUMENT_TITLES } from './ViewOfferModal.helpers';

export interface Props {
  interactionId: string;
  interactionType: InteractionType;
  open: boolean;
  handleClose: () => void;
}

export const ViewOfferModal = ({
  interactionId,
  interactionType,
  open,
  handleClose,
}: Props) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const { propertyName, offerPackage, contacts, loading } = useGetOfferPackage({
    id: interactionId,
    type: interactionType,
    open,
  });

  return (
    <Dialog aria-labelledby="dialog-title" open={open} onClose={handleClose}>
      <DialogContent
        sx={{
          '& .MuiFormLabel-root': { mt: 3 },
        }}
        titleSx={{ pb: 3 }}
        onClose={handleClose}
        loading={loading}
        title={`Offer Documents for ${propertyName}`}
      >
        {loading || !offerPackage ? (
          <EmptyState loading={loading} message="No offer package found." />
        ) : (
          <>
            {offerPackage.hasUnsignedDocuments ? (
              <Alert severity="warning" sx={{ mb: 2 }}>
                This offer package includes unsigned contract documents. Please
                regenerate this offer with fully executed documents
              </Alert>
            ) : null}
            <Grid
              container
              spacing={{ xs: 4, md: 0 }}
              sx={{ flexDirection: { xs: 'column', md: 'row' } }}
            >
              <Grid
                size={{ xs: 12, md: 5 }}
                sx={{ display: 'flex', alignItems: 'center' }}
              >
                <Box sx={{ width: '100%' }}>
                  <Typography sx={{ marginBottom: 1 }}>
                    Combined Offer Package
                  </Typography>
                  <List aria-label="Combined Offer Package">
                    {offerPackage?.combinedShortUrl?.id &&
                    offerPackage.combinedShortUrl.url ? (
                      <PackageLink
                        label="Offer Package"
                        link={offerPackage.combinedShortUrl.url}
                        warning={offerPackage?.hasUnsignedDocuments || false}
                        shortUrlId={offerPackage.combinedShortUrl.id}
                      />
                    ) : null}
                  </List>
                  <MailToLink
                    combined
                    href={composeEmail(
                      contacts ?? [],
                      true,
                      offerPackage,
                      propertyName ?? '',
                    )}
                  />
                </Box>
              </Grid>
              <Grid size={{ xs: 'auto', md: 2 }} sx={{ minHeight: '100%' }}>
                <Divider
                  orientation={isMobile ? 'horizontal' : 'vertical'}
                  flexItem
                  sx={{ height: '100%' }}
                >
                  <Typography sx={{ color: 'text.secondary' }}>OR</Typography>
                </Divider>
              </Grid>
              <Grid size={{ xs: 12, md: 5 }}>
                <Typography sx={{ marginBottom: 1 }}>
                  Separate Offer Files
                </Typography>
                <List aria-label="Separate Offer Files">
                  {Object.entries(offerPackage || {}).map(
                    ([section, sectionObj]) =>
                      DOCUMENT_TITLES[section] &&
                      sectionObj &&
                      typeof sectionObj === 'object' &&
                      sectionObj.url &&
                      sectionObj.id ? (
                        <PackageLink
                          key={section}
                          label={DOCUMENT_TITLES[section]}
                          link={sectionObj.url}
                          shortUrlId={sectionObj.id}
                          warning={
                            (offerPackage.hasUnsignedDocuments &&
                              DOCUMENT_TITLES[section] === 'Offer Docs') ||
                            false
                          }
                        />
                      ) : null,
                  )}
                </List>
                <MailToLink
                  combined={false}
                  href={composeEmail(
                    contacts ?? [],
                    false,
                    offerPackage,
                    propertyName ?? '',
                  )}
                />
              </Grid>
            </Grid>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
